import { randomFractalColors } from "./fractal-palettes";
import type { FractalFamily, FractalSettings, FractalVariant } from "../types/fractal";

type FractalRange = readonly [number, number];

type FractalFamilyProfile = {
  label: string;
  palettes: readonly [string, string, string, string, string, string][];
  defaults: Partial<Omit<FractalSettings, "colors" | "fractalFamily" | "fractalVariant">>;
  ranges: Record<
    Exclude<
      keyof FractalSettings,
      "colors" | "fractalFamily" | "fractalVariant" | "audioReactive" | "voxelMaterialType" | "voxelRoughness"
    >,
    FractalRange
  >;
};

export const FRACTAL_FAMILY_OPTIONS = ["cathedral", "nebula", "crystal", "mandala", "monolith"] as const;
export const DEFAULT_FRACTAL_FAMILY: FractalFamily = "cathedral";
export const FRACTAL_VARIANT_OPTIONS = ["infiniteZoom", "flarebrot", "voxelTunnel", "planarLight"] as const;
export const DEFAULT_FRACTAL_VARIANT: FractalVariant = "infiniteZoom";

export const FRACTAL_FAMILY_PROFILES: Record<FractalFamily, FractalFamilyProfile> = {
  cathedral: {
    label: "Cathedral",
    palettes: [
      ["#f5ebff", "#8ad7ff", "#4d7fff", "#182d72", "#091032", "#020308"],
      ["#fff7eb", "#b7d7ff", "#7194ff", "#29369d", "#111740", "#020308"],
      ["#eaf6ff", "#9fe2ff", "#6c9cff", "#2f4ae0", "#111b52", "#03050d"],
    ],
    defaults: {
      intensity: 0.78,
      brightness: 0.46,
      contrast: 0.78,
      scale: 0.42,
      symmetry: 0.62,
      glow: 0.3,
      vignette: 0.44,
      grain: 0.03,
      paletteIntensity: 1.04,
      hueShift: 0,
      saturation: 0.84,
      colorDrift: 0.1,
      motionSpeed: 0.2,
      rotationSpeed: 0.1,
      drift: 0.08,
      breathing: 0.22,
      shimmer: 0.16,
      bassImpact: 0.28,
      midImpact: 0.24,
      trebleImpact: 0.14,
      beatPulse: 0.16,
      audioSmoothing: 0.82,
      fractalDepth: 0.72,
      foldStrength: 0.68,
      repetition: 0.44,
      fieldWarp: 0.1,
      detail: 0.74,
    },
    ranges: {
      intensity: [0.68, 0.88],
      brightness: [0.38, 0.56],
      contrast: [0.7, 0.9],
      scale: [0.34, 0.52],
      symmetry: [0.5, 0.82],
      glow: [0.24, 0.38],
      vignette: [0.34, 0.58],
      grain: [0.01, 0.05],
      paletteIntensity: [0.98, 1.12],
      hueShift: [-0.06, 0.06],
      saturation: [0.74, 0.92],
      colorDrift: [0.02, 0.12],
      motionSpeed: [0.12, 0.28],
      rotationSpeed: [0.04, 0.16],
      drift: [0.04, 0.14],
      breathing: [0.12, 0.3],
      shimmer: [0.08, 0.2],
      bassImpact: [0.18, 0.36],
      midImpact: [0.14, 0.32],
      trebleImpact: [0.08, 0.18],
      beatPulse: [0.08, 0.22],
      audioSmoothing: [0.76, 0.94],
      fractalDepth: [0.62, 0.84],
      foldStrength: [0.56, 0.82],
      repetition: [0.34, 0.54],
      fieldWarp: [0.04, 0.16],
      detail: [0.64, 0.86],
    },
  },
  nebula: {
    label: "Nebula",
    palettes: [
      ["#e6f5ff", "#89ffd8", "#58b8ff", "#3f60d9", "#142042", "#04070f"],
      ["#f5fbff", "#93f6ff", "#66cfff", "#4f70ff", "#1c2d63", "#050713"],
      ["#eefeff", "#c5ffef", "#7ce0ff", "#4a86ff", "#1b3268", "#05070e"],
    ],
    defaults: {
      intensity: 0.7,
      brightness: 0.5,
      contrast: 0.62,
      scale: 0.54,
      symmetry: 0.18,
      glow: 0.38,
      vignette: 0.34,
      grain: 0.02,
      paletteIntensity: 1.02,
      hueShift: 0,
      saturation: 0.82,
      colorDrift: 0.16,
      motionSpeed: 0.3,
      rotationSpeed: 0.08,
      drift: 0.28,
      breathing: 0.16,
      shimmer: 0.14,
      bassImpact: 0.22,
      midImpact: 0.22,
      trebleImpact: 0.12,
      beatPulse: 0.14,
      audioSmoothing: 0.84,
      fractalDepth: 0.62,
      foldStrength: 0.54,
      repetition: 0.28,
      fieldWarp: 0.22,
      detail: 0.64,
    },
    ranges: {
      intensity: [0.58, 0.82],
      brightness: [0.42, 0.62],
      contrast: [0.52, 0.74],
      scale: [0.44, 0.68],
      symmetry: [0.04, 0.28],
      glow: [0.28, 0.48],
      vignette: [0.22, 0.44],
      grain: [0.0, 0.04],
      paletteIntensity: [0.94, 1.14],
      hueShift: [-0.08, 0.08],
      saturation: [0.72, 0.92],
      colorDrift: [0.08, 0.22],
      motionSpeed: [0.22, 0.42],
      rotationSpeed: [0.02, 0.14],
      drift: [0.18, 0.4],
      breathing: [0.06, 0.2],
      shimmer: [0.06, 0.18],
      bassImpact: [0.12, 0.3],
      midImpact: [0.14, 0.3],
      trebleImpact: [0.06, 0.16],
      beatPulse: [0.06, 0.18],
      audioSmoothing: [0.8, 0.96],
      fractalDepth: [0.52, 0.74],
      foldStrength: [0.42, 0.62],
      repetition: [0.16, 0.34],
      fieldWarp: [0.14, 0.32],
      detail: [0.56, 0.74],
    },
  },
  crystal: {
    label: "Crystal",
    palettes: [
      ["#fbfdff", "#8cf0ff", "#2bb8ff", "#214fda", "#091950", "#02050d"],
      ["#f3ffff", "#b8f7ff", "#79d9ff", "#3276ff", "#10245c", "#02050d"],
      ["#f0f7ff", "#d0ebff", "#8fc5ff", "#4970ff", "#15255e", "#02040b"],
    ],
    defaults: {
      intensity: 0.8,
      brightness: 0.42,
      contrast: 0.9,
      scale: 0.38,
      symmetry: 0.28,
      glow: 0.26,
      vignette: 0.48,
      grain: 0.02,
      paletteIntensity: 1.08,
      hueShift: 0,
      saturation: 0.88,
      colorDrift: 0.06,
      motionSpeed: 0.16,
      rotationSpeed: 0.08,
      drift: 0.08,
      breathing: 0.12,
      shimmer: 0.28,
      bassImpact: 0.18,
      midImpact: 0.26,
      trebleImpact: 0.22,
      beatPulse: 0.12,
      audioSmoothing: 0.84,
      fractalDepth: 0.7,
      foldStrength: 0.76,
      repetition: 0.26,
      fieldWarp: 0.08,
      detail: 0.84,
    },
    ranges: {
      intensity: [0.72, 0.9],
      brightness: [0.34, 0.48],
      contrast: [0.8, 0.98],
      scale: [0.28, 0.46],
      symmetry: [0.12, 0.36],
      glow: [0.18, 0.32],
      vignette: [0.36, 0.6],
      grain: [0.0, 0.04],
      paletteIntensity: [1.0, 1.16],
      hueShift: [-0.04, 0.04],
      saturation: [0.8, 0.96],
      colorDrift: [0.0, 0.1],
      motionSpeed: [0.08, 0.22],
      rotationSpeed: [0.02, 0.12],
      drift: [0.02, 0.12],
      breathing: [0.04, 0.16],
      shimmer: [0.16, 0.34],
      bassImpact: [0.1, 0.22],
      midImpact: [0.18, 0.34],
      trebleImpact: [0.16, 0.3],
      beatPulse: [0.06, 0.16],
      audioSmoothing: [0.78, 0.94],
      fractalDepth: [0.62, 0.84],
      foldStrength: [0.68, 0.88],
      repetition: [0.18, 0.34],
      fieldWarp: [0.02, 0.12],
      detail: [0.74, 0.92],
    },
  },
  mandala: {
    label: "Mandala",
    palettes: [
      ["#fff4dc", "#ffcf79", "#d79dff", "#6360ff", "#231c56", "#05040a"],
      ["#fff5e8", "#ffcb95", "#f1a9ff", "#7b7dff", "#2d2667", "#05040a"],
      ["#fbf7ff", "#e3c4ff", "#a7a3ff", "#5f7cff", "#212a67", "#05040a"],
    ],
    defaults: {
      intensity: 0.78,
      brightness: 0.47,
      contrast: 0.8,
      scale: 0.48,
      symmetry: 0.84,
      glow: 0.34,
      vignette: 0.4,
      grain: 0.03,
      paletteIntensity: 1.06,
      hueShift: 0,
      saturation: 0.86,
      colorDrift: 0.08,
      motionSpeed: 0.18,
      rotationSpeed: 0.2,
      drift: 0.1,
      breathing: 0.14,
      shimmer: 0.18,
      bassImpact: 0.2,
      midImpact: 0.24,
      trebleImpact: 0.16,
      beatPulse: 0.14,
      audioSmoothing: 0.82,
      fractalDepth: 0.74,
      foldStrength: 0.62,
      repetition: 0.58,
      fieldWarp: 0.1,
      detail: 0.8,
    },
    ranges: {
      intensity: [0.68, 0.88],
      brightness: [0.4, 0.56],
      contrast: [0.7, 0.9],
      scale: [0.4, 0.58],
      symmetry: [0.68, 0.96],
      glow: [0.24, 0.4],
      vignette: [0.28, 0.5],
      grain: [0.01, 0.05],
      paletteIntensity: [0.98, 1.14],
      hueShift: [-0.06, 0.06],
      saturation: [0.74, 0.92],
      colorDrift: [0.02, 0.12],
      motionSpeed: [0.1, 0.24],
      rotationSpeed: [0.12, 0.3],
      drift: [0.04, 0.14],
      breathing: [0.06, 0.18],
      shimmer: [0.08, 0.22],
      bassImpact: [0.12, 0.26],
      midImpact: [0.16, 0.32],
      trebleImpact: [0.08, 0.2],
      beatPulse: [0.08, 0.18],
      audioSmoothing: [0.78, 0.94],
      fractalDepth: [0.66, 0.84],
      foldStrength: [0.52, 0.74],
      repetition: [0.48, 0.7],
      fieldWarp: [0.04, 0.14],
      detail: [0.68, 0.88],
    },
  },
  monolith: {
    label: "Monolith",
    palettes: [
      ["#f5f7ff", "#d3dcf5", "#9aa7cf", "#5a668f", "#1e2437", "#040509"],
      ["#fef7e4", "#d7c4a5", "#8e8290", "#4e5166", "#171a25", "#040507"],
      ["#f7f1ff", "#c8c4da", "#8a8ca6", "#4f5268", "#171a28", "#040507"],
    ],
    defaults: {
      intensity: 0.76,
      brightness: 0.38,
      contrast: 0.88,
      scale: 0.36,
      symmetry: 0.08,
      glow: 0.22,
      vignette: 0.5,
      grain: 0.02,
      paletteIntensity: 1.02,
      hueShift: 0,
      saturation: 0.7,
      colorDrift: 0.04,
      motionSpeed: 0.12,
      rotationSpeed: 0.04,
      drift: 0.06,
      breathing: 0.1,
      shimmer: 0.12,
      bassImpact: 0.3,
      midImpact: 0.18,
      trebleImpact: 0.1,
      beatPulse: 0.12,
      audioSmoothing: 0.86,
      fractalDepth: 0.68,
      foldStrength: 0.72,
      repetition: 0.22,
      fieldWarp: 0.06,
      detail: 0.7,
    },
    ranges: {
      intensity: [0.66, 0.86],
      brightness: [0.28, 0.44],
      contrast: [0.8, 0.98],
      scale: [0.28, 0.46],
      symmetry: [0.0, 0.18],
      glow: [0.14, 0.28],
      vignette: [0.38, 0.62],
      grain: [0.0, 0.04],
      paletteIntensity: [0.94, 1.1],
      hueShift: [-0.04, 0.04],
      saturation: [0.56, 0.78],
      colorDrift: [0.0, 0.08],
      motionSpeed: [0.06, 0.18],
      rotationSpeed: [0.0, 0.08],
      drift: [0.02, 0.1],
      breathing: [0.04, 0.14],
      shimmer: [0.06, 0.16],
      bassImpact: [0.22, 0.38],
      midImpact: [0.1, 0.22],
      trebleImpact: [0.04, 0.12],
      beatPulse: [0.06, 0.16],
      audioSmoothing: [0.8, 0.96],
      fractalDepth: [0.58, 0.78],
      foldStrength: [0.62, 0.84],
      repetition: [0.14, 0.28],
      fieldWarp: [0.02, 0.1],
      detail: [0.58, 0.78],
    },
  },
};

export function isFractalFamily(value: unknown): value is FractalFamily {
  return typeof value === "string" && FRACTAL_FAMILY_OPTIONS.includes(value as FractalFamily);
}

export function isFractalVariant(value: unknown): value is FractalVariant {
  return typeof value === "string" && FRACTAL_VARIANT_OPTIONS.includes(value as FractalVariant);
}

export function getFractalFamilyLabel(family: FractalFamily) {
  return FRACTAL_FAMILY_PROFILES[family].label;
}

export function getFractalFamilyId(family: FractalFamily) {
  return FRACTAL_FAMILY_OPTIONS.indexOf(family);
}

export function getFractalVariantId(variant: FractalVariant) {
  return FRACTAL_VARIANT_OPTIONS.indexOf(variant);
}

function getDefaultPaletteForFamily(family: FractalFamily) {
  return [...FRACTAL_FAMILY_PROFILES[family].palettes[0]];
}

export function pickRandomFractalFamily(): FractalFamily {
  return FRACTAL_FAMILY_OPTIONS[Math.floor(Math.random() * FRACTAL_FAMILY_OPTIONS.length)];
}

export function inferFractalFamilyFromPrompt(prompt: string): FractalFamily | null {
  const value = prompt.toLowerCase();
  const keywordGroups: Array<[FractalFamily, string[]]> = [
    ["cathedral", ["cathedral", "sanctum", "chapel", "vault", "architectural", "altar"]],
    ["nebula", ["nebula", "cloud", "mist", "aurora", "atmospheric", "soft", "diffuse"]],
    ["crystal", ["crystal", "glass", "faceted", "prism", "shard", "icy", "silver"]],
    ["mandala", ["mandala", "ornamental", "radial", "sacred", "lotus", "ornate"]],
    ["monolith", ["monolith", "pillar", "imposing", "void", "tower", "sparse", "slab"]],
  ];

  for (const [family, keywords] of keywordGroups) {
    if (keywords.some((keyword) => value.includes(keyword))) {
      return family;
    }
  }

  return null;
}

export const DEFAULT_FRACTAL_SETTINGS: FractalSettings = {
  fractalFamily: DEFAULT_FRACTAL_FAMILY,
  fractalVariant: DEFAULT_FRACTAL_VARIANT,
  colors: getDefaultPaletteForFamily(DEFAULT_FRACTAL_FAMILY),
  intensity: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.intensity!,
  brightness: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.brightness!,
  contrast: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.contrast!,
  scale: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.scale!,
  symmetry: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.symmetry!,
  glow: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.glow!,
  vignette: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.vignette!,
  grain: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.grain!,
  paletteIntensity: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.paletteIntensity!,
  hueShift: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.hueShift!,
  saturation: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.saturation!,
  colorDrift: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.colorDrift!,
  motionSpeed: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.motionSpeed!,
  rotationSpeed: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.rotationSpeed!,
  drift: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.drift!,
  breathing: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.breathing!,
  shimmer: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.shimmer!,
  audioReactive: true,
  bassImpact: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.bassImpact!,
  midImpact: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.midImpact!,
  trebleImpact: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.trebleImpact!,
  beatPulse: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.beatPulse!,
  audioSmoothing: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.audioSmoothing!,
  fractalDepth: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.fractalDepth!,
  foldStrength: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.foldStrength!,
  repetition: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.repetition!,
  fieldWarp: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.fieldWarp!,
  detail: FRACTAL_FAMILY_PROFILES[DEFAULT_FRACTAL_FAMILY].defaults.detail!,
  voxelMaterialType: 1,
  voxelRoughness: 0.15,
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function round(value: number, digits = 2) {
  return Number(value.toFixed(digits));
}

function randomBetween(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function sampleRange([min, max]: FractalRange) {
  return randomBetween(min, max);
}

function isHexColor(value: unknown): value is string {
  return typeof value === "string" && /^#([0-9a-fA-F]{6})$/.test(value);
}

export function normalizeFractalSettings(
  input?: Partial<FractalSettings> | null
): FractalSettings {
  const family = isFractalFamily(input?.fractalFamily) ? input.fractalFamily : DEFAULT_FRACTAL_FAMILY;
  const variant = isFractalVariant(input?.fractalVariant) ? input.fractalVariant : DEFAULT_FRACTAL_VARIANT;
  const familyDefaults = FRACTAL_FAMILY_PROFILES[family].defaults;
  const merged = {
    ...DEFAULT_FRACTAL_SETTINGS,
    ...familyDefaults,
    ...(input ?? {}),
    fractalFamily: family,
    fractalVariant: variant,
  } as FractalSettings;

  return {
    fractalFamily: family,
    fractalVariant: variant,
    colors:
      Array.isArray(merged.colors) &&
        merged.colors.length >= 6 &&
        merged.colors.slice(0, 6).every(isHexColor)
        ? merged.colors.slice(0, 6)
        : getDefaultPaletteForFamily(family),
    intensity: round(clamp(merged.intensity, 0, 1)),
    brightness: round(clamp(merged.brightness, 0, 1)),
    contrast: round(clamp(merged.contrast, 0, 1)),
    scale: round(clamp(merged.scale, 0, 1)),
    symmetry: round(clamp(merged.symmetry, 0, 1)),
    glow: round(clamp(merged.glow, 0, 1)),
    vignette: round(clamp(merged.vignette, 0, 1)),
    grain: round(clamp(merged.grain, 0, 1)),
    paletteIntensity: round(clamp(merged.paletteIntensity, 0, 1.5)),
    hueShift: round(clamp(merged.hueShift, -1, 1)),
    saturation: round(clamp(merged.saturation, 0, 1.5)),
    colorDrift: round(clamp(merged.colorDrift, 0, 1)),
    motionSpeed: round(clamp(merged.motionSpeed, 0, 1)),
    rotationSpeed: round(clamp(merged.rotationSpeed, 0, 1)),
    drift: round(clamp(merged.drift, 0, 1)),
    breathing: round(clamp(merged.breathing, 0, 1)),
    shimmer: round(clamp(merged.shimmer, 0, 1)),
    audioReactive: merged.audioReactive !== false,
    bassImpact: round(clamp(merged.bassImpact, 0, 1)),
    midImpact: round(clamp(merged.midImpact, 0, 1)),
    trebleImpact: round(clamp(merged.trebleImpact, 0, 1)),
    beatPulse: round(clamp(merged.beatPulse, 0, 1)),
    audioSmoothing: round(clamp(merged.audioSmoothing, 0, 1)),
    fractalDepth: round(clamp(merged.fractalDepth, 0, 1)),
    foldStrength: round(clamp(merged.foldStrength, 0, 1)),
    repetition: round(clamp(merged.repetition, 0, 1)),
    fieldWarp: round(clamp(merged.fieldWarp, 0, 1)),
    detail: round(clamp(merged.detail, 0, 1)),
    voxelMaterialType: Math.round(clamp(merged.voxelMaterialType, 0, 2)),
    voxelRoughness: round(clamp(merged.voxelRoughness, 0, 1)),
  };
}

export function randomizeFractalSettings(preferredFamily?: FractalFamily): FractalSettings {
  const family = preferredFamily ?? pickRandomFractalFamily();
  const profile = FRACTAL_FAMILY_PROFILES[family];

  return normalizeFractalSettings({
    fractalFamily: family,
    fractalVariant: DEFAULT_FRACTAL_VARIANT,
    colors: randomFractalColors(),
    intensity: sampleRange(profile.ranges.intensity),
    brightness: sampleRange(profile.ranges.brightness),
    contrast: sampleRange(profile.ranges.contrast),
    scale: sampleRange(profile.ranges.scale),
    symmetry: sampleRange(profile.ranges.symmetry),
    glow: sampleRange(profile.ranges.glow),
    vignette: sampleRange(profile.ranges.vignette),
    grain: sampleRange(profile.ranges.grain),
    paletteIntensity: sampleRange(profile.ranges.paletteIntensity),
    hueShift: sampleRange(profile.ranges.hueShift),
    saturation: sampleRange(profile.ranges.saturation),
    colorDrift: sampleRange(profile.ranges.colorDrift),
    motionSpeed: sampleRange(profile.ranges.motionSpeed),
    rotationSpeed: sampleRange(profile.ranges.rotationSpeed),
    drift: sampleRange(profile.ranges.drift),
    breathing: sampleRange(profile.ranges.breathing),
    shimmer: sampleRange(profile.ranges.shimmer),
    audioReactive: true,
    bassImpact: sampleRange(profile.ranges.bassImpact),
    midImpact: sampleRange(profile.ranges.midImpact),
    trebleImpact: sampleRange(profile.ranges.trebleImpact),
    beatPulse: sampleRange(profile.ranges.beatPulse),
    audioSmoothing: sampleRange(profile.ranges.audioSmoothing),
    fractalDepth: sampleRange(profile.ranges.fractalDepth),
    foldStrength: sampleRange(profile.ranges.foldStrength),
    repetition: sampleRange(profile.ranges.repetition),
    fieldWarp: sampleRange(profile.ranges.fieldWarp),
    detail: sampleRange(profile.ranges.detail),
  });
}
